import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Bell, Clock, ExternalLink, RefreshCw } from 'lucide-react';
import * as api from '../api';

const SlaReportModal = ({ onClose, onOpenTask }) => {
  const [days, setDays] = useState(5);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');

  const load = async (d = days) => {
    setLoading(true);
    setError('');
    try {
      const result = await api.getStagnantTasks(d);
      setData(result);
    } catch (e) {
      setError(e.message || 'Falha ao carregar.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(days); }, [days]);

  const handleSendAlert = async () => {
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const result = await api.generateStagnantAlert(days);
      setSuccess(result.message || `${result.count} tarefa(s) sinalizadas. Notificações enviadas aos admins.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e.message || 'Falha ao enviar alerta.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-3xl bg-[#181b26] border border-red-500/40 rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><AlertTriangle size={20} className="text-red-300" /> Tarefas sem movimento (SLA)</h3>
            <p className="text-sm opacity-70 mt-0.5">Lista de tarefas que ultrapassaram o limite de dias sem atualização.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 border-b border-slate-700 flex flex-wrap items-center gap-3">
          <div>
            <label className="text-xs opacity-70 block mb-1">Threshold (dias)</label>
            <input
              type="number"
              min="1"
              max="365"
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 5)))}
              className="w-24 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white"
            />
          </div>
          <button onClick={() => load(days)} disabled={loading} className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm flex items-center gap-1">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
          </button>
          <button onClick={handleSendAlert} disabled={sending || !data || data.count === 0} className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 disabled:bg-red-900 disabled:cursor-not-allowed text-slate-900 text-sm font-semibold flex items-center gap-2">
            <Bell size={14} />
            {sending ? 'Enviando...' : 'Enviar alerta aos admins'}
          </button>
          {data ? <div className="ml-auto text-sm opacity-70">{data.count} tarefa(s) em risco</div> : null}
        </div>

        {error ? <div className="mx-5 mt-4 bg-red-500/15 border border-red-500/40 text-red-200 rounded-lg px-3 py-2 text-sm">{error}</div> : null}
        {success ? <div className="mx-5 mt-4 bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 rounded-lg px-3 py-2 text-sm">{success}</div> : null}

        <div className="p-5 overflow-y-auto">
          {loading ? (
            <div className="text-sm opacity-60 text-center py-6">Carregando...</div>
          ) : !data || data.items.length === 0 ? (
            <div className="text-sm opacity-60 text-center py-10 bg-slate-900/40 rounded-lg">Nenhuma tarefa em risco. Tudo em dia!</div>
          ) : (
            <div className="space-y-2">
              {data.items.map((item) => {
                const overBy = item.exceededBy || 0;
                const color = overBy >= 3 ? 'border-red-500/60 bg-red-500/5' : 'border-amber-500/50 bg-amber-500/5';
                return (
                  <div key={item.id} className={`border rounded-lg p-3 ${color}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="font-semibold text-slate-200">{item.elemento || `Tarefa ${item.id}`}</div>
                        <div className="text-xs opacity-70 mt-0.5">
                          Status: <strong>{item.status || '-'}</strong>
                          {item.equipe && item.equipe.length > 0 ? <> · Equipe: {item.equipe.join(', ')}</> : null}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-red-300 flex items-center gap-1 justify-end">
                          <Clock size={14} /> {item.daysSince}d
                        </div>
                        <div className="text-[10px] opacity-70">SLA: {item.slaDays}d · Excedido em {item.exceededBy}d</div>
                        {onOpenTask ? (
                          <button
                            onClick={() => onOpenTask(item)}
                            className="mt-2 px-2.5 py-1 rounded text-[11px] font-semibold bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 text-blue-200 inline-flex items-center gap-1"
                            title="Abrir no Kanban"
                          >
                            <ExternalLink size={12} /> Abrir no Kanban
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <div className="text-[10px] opacity-60 mt-1">Última atividade: {item.lastActivityAt ? new Date(item.lastActivityAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-700 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-700 hover:bg-white/10 text-sm">Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default SlaReportModal;
